import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@tylertech/forge-angular';
import { FilterOrSearchModel, LastNDays } from 'src/app/models/filter-or-search.model';
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { map, take } from "rxjs";
import { Subscription } from "rxjs/internal/Subscription";
import { DateUtils } from "../../../utils/date.utils";
import { ArrestingAgencyService } from 'src/app/core/services/arresting-agency.service';
import { ISelectOption } from '@tylertech/forge';
import { SearchFilterChipService } from 'src/app/core/services/search-filter-chip.service';

@Component({
  selector: 'app-public-search-sort-filter',
  templateUrl: './public-search-sort-filter.component.html',
  styleUrls: ['./public-search-sort-filter.component.scss']
})
export class PublicSearchSortFilterComponent implements OnInit, OnDestroy {
  filterForm = this._fb.group({
    nameOrId: this._fb.control<string | null>(null),
    sortBy: this._fb.control<string>('recentArrestDate', { nonNullable: true }),
    arrestingAgencies: this._fb.control<string[] | null>(null),
    minAge: this._fb.control<number| null>(null),
    maxAge: this._fb.control<number| null>(null),
    dateRange: this._fb.control<LastNDays | string | null>(null),
    minArrestDate: this._fb.control<string | null>(null),
    maxArrestDate: this._fb.control<string | null>(null)
  });

  protected readonly LastNDays = LastNDays;
  protected readonly minDatePickerValue = DateUtils.MIN_DATE_PICKER_VALUE;
  private readonly _subscriptions: Subscription[] = [];

  appliedFilterCount = 1;

  arrestingAgencies: ISelectOption[] = [];

  constructor(
    @Inject(DIALOG_DATA) public data: any,
    private readonly _dialogRef: DialogRef,
    private readonly _fb: FormBuilder,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _arrestingAgencyService: ArrestingAgencyService,
    private readonly _searchFilterChipService: SearchFilterChipService
  ) {
    this._arrestingAgencyService.getArrestingAgencies(this.data.instanceId)
      .pipe(take(1))
      .subscribe(agencies => {
        this.arrestingAgencies = agencies.map(agency => ({
          value: agency,
          label: this.truncateAgencyName(agency)
        }));
      });
  }

  ngOnDestroy = () => this._subscriptions.forEach(sub => sub.unsubscribe());

  ngOnInit() {
    this._subscriptions.push(
      this._activatedRoute.queryParamMap
        .pipe(
          map((params: ParamMap) => ({
            nameOrId: params.get('nameOrId'),
            sortBy: params.get('sortBy') ?? 'recentArrestDate',
            arrestingAgencies: params.getAll('arrestingAgencies'),
            minAge: params.get('minAge'),
            maxAge: params.get('maxAge'),
            minArrestDate: params.get('minArrestDate') ? DateUtils.formatDateMDYYYY(params.get('minArrestDate') ?? '') : null,
            maxArrestDate: params.get('maxArrestDate') ? DateUtils.formatDateMDYYYY(params.get('maxArrestDate') ?? '') : null,
            dateRange: params.get('dateRange')
          } as FilterOrSearchModel))
        ).subscribe({
          next: (queryParams: FilterOrSearchModel) => {
            // If we have custom dates but no dateRange, set dateRange to Custom
            if ((queryParams.minArrestDate || queryParams.maxArrestDate) && !queryParams.dateRange) {
              queryParams.dateRange = LastNDays.Custom;
            }
            this.filterForm.patchValue(queryParams);
          }
        })
    );

    this.registerFilterFormListener();
    this.trackOrderOfInput();
  }

  trackOrderOfInput() {
    Object.keys(this.filterForm.controls).forEach(key => {
      this._subscriptions.push(
        this.filterForm.get(key)!.valueChanges.subscribe((value) => {
          this.handleFormFieldChange(key as keyof FilterOrSearchModel, value);
        })
      );
    });
  }

  private handleFormFieldChange(key: keyof FilterOrSearchModel, value: any) {
    const hasValue = value !== null && value !== undefined && value !== '';
    const isCustomDateRange = this.filterForm.controls.dateRange.value === '0'
                              || this.filterForm.controls.dateRange.value === LastNDays.Custom
                              || value === '0'
                              || value === LastNDays.Custom;

    if (key === 'dateRange') {
      if (isCustomDateRange) {
        this._searchFilterChipService.removeChipAndReorder('dateRange', null, false);
        return;
      } else {
        this._searchFilterChipService.removeChipAndReorder('minArrestDate', null, false);
        this._searchFilterChipService.removeChipAndReorder('maxArrestDate', null, false);
      }
    } else if (key === 'minArrestDate' || key === 'maxArrestDate') {
      if (!isCustomDateRange) {
        return;
      }
    }

    // Handle all other fields
    this._searchFilterChipService.removeChipAndReorder(key, null, false);
    if (hasValue) {
      this._searchFilterChipService.addChip({ key, value });
    }
  }

  registerFilterFormListener(): void {
    this.filterForm.valueChanges.subscribe(formValue => {
      let filledControlCount = 0;

      Object.keys(this.filterForm.controls).forEach(controlName =>  {
        const control = this.filterForm.get(controlName);

        if(controlName === 'dateRange' && control?.value === 'custom') {
          return;
        }

        if (control) {
          const value = control.value;

          if (Array.isArray(value)) {
            filledControlCount += value.length;
          } else if (value !== null && value !== undefined && value !== '') {
            filledControlCount++;
          }
        }
      });

      this.appliedFilterCount = filledControlCount;
    });
  }

  closeDialog(): void {
    this._dialogRef.close(null);
  }

  resetAllFilters(): void {
    this.filterForm.reset();
    this._searchFilterChipService.clearChips();
  }

  onClearControl(controlNames: (keyof typeof this.filterForm.controls)[]): void {
    controlNames.forEach(controlName => {
      this.filterForm.controls[controlName].reset();
      this._searchFilterChipService
        .removeChipAndReorder(controlName, null, false)
    });
  }

  async onSubmit() {
    if (this.filterForm.invalid) return;

    let filterOrSearchModel = {
      nameOrId: this.filterForm.controls.nameOrId.value,
      sortBy: this.filterForm.controls.sortBy.value,
      arrestingAgencies: this.filterForm.controls.arrestingAgencies.value,
      minAge: this.filterForm.controls.minAge.value,
      maxAge: this.filterForm.controls.maxAge.value,
      minArrestDate: this.filterForm.controls.minArrestDate.value ? DateUtils.formatDateISO(new Date(this.filterForm.controls.minArrestDate.value)) : null,
      maxArrestDate: this.filterForm.controls.maxArrestDate.value ? DateUtils.formatDateISO(new Date(this.filterForm.controls.maxArrestDate.value)) : null,
      dateRange: this.filterForm.controls.dateRange.value
    } as FilterOrSearchModel;

    // Only use preset date logic if dateRange is not Custom and not null
    if(this.filterForm.controls.dateRange.value !== null &&
       this.filterForm.controls.dateRange.value !== LastNDays.Custom &&
       this.filterForm.controls.dateRange.value !== '0') {
      const startDate = DateUtils.currentDateAddDays(Number(this.filterForm.controls.dateRange.value));
      filterOrSearchModel.minArrestDate = DateUtils.formatDateISO(startDate);
      filterOrSearchModel.maxArrestDate = null;
    }

    // If we're using custom dates, don't include dateRange in URL parameters
    if (this.filterForm.controls.dateRange.value === LastNDays.Custom || this.filterForm.controls.dateRange.value === '0') {
      filterOrSearchModel.dateRange = null;
    }

    await this._router.navigate([], {
      queryParams: filterOrSearchModel,
      queryParamsHandling: 'merge'
    });
    this._searchFilterChipService.onSearchAndFilterClick();

    this._dialogRef.close(filterOrSearchModel);
  }

  private truncateAgencyName(name: string): string {
    const maxLength = 35;
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  }
}